// ─────────────────────────────────────────────────────────────────────────────
// Bloque de plantilla del armazón de Reportería.
//
// La maqueta lo pinta encima de las SIETE pestañas: «Plantilla actualizada: 05 Jul 2026 · 4 230 total
// · 2 814 Staff 67 % · 1 416 Frontline 33 %». Es lo que más pesa en la impresión de «no se parece»,
// porque se repite en cada pantalla y es lo primero que se ve bajo los filtros.
//
// Vive en el ARMAZÓN y no en un tab porque no depende de la pestaña activa ni del rango de fechas: el
// censo es una foto del directorio. Por eso tiene su propia consulta —`GET …/headcount`, sin
// `from`/`to`— en vez de sacar las cifras del reporte Diario, que sí es del periodo.
//
// 🔴 `total === 0` significa DESCONOCIDO, no «organización vacía»: es lo que devuelve el censo cuando
// no hay snapshots. En ese caso el bloque NO se pinta, en vez de mostrar «0 personas», que se leería
// como un hecho medido. Mismo criterio que las tarjetas de adopción del Diario.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import { useRecognitionHeadcount } from '@/api/recognition';

export interface BloquePlantillaProps {
  readonly ouIds?: readonly string[];
  /**
   * Frase que ata este bloque a lo que viene DEBAJO.
   *
   * 🔴 La maqueta de Brillamos pinta el bloque DOS veces en la misma pantalla. Repetir el mismo texto
   * dos veces sería copia-pega; lo que hace útil la repetición es que cada instancia diga para qué
   * sirven esas cifras ahí: arriba son la referencia del periodo, y la segunda es el DENOMINADOR de
   * las tarjetas de adopción que van justo debajo. Sin `nota` se usa el texto genérico de siempre.
   */
  readonly nota?: string;
}

export function BloquePlantilla({ ouIds, nota }: BloquePlantillaProps) {
  const { t, i18n } = useTranslation('recognition');
  const { data } = useRecognitionHeadcount({ ouIds });

  // Sin censo no hay bloque. Un «0 personas» sería peor que no decir nada.
  if (!data || data.total <= 0) return null;

  const numero = (n: number) => n.toLocaleString(i18n.language);
  const pct = (n: number) => Math.round((n / data.total) * 100);
  const staff = data.total - data.frontline;

  // Fecha corta en el idioma activo, o el marcador de "sin dato" — Fase 7 (2026-09-11, pedido del
  // cliente): las 3 fuentes se muestran SEPARADAS, no colapsadas a la más reciente. Cada una puede
  // no tener corrida propia (p. ej. Europa nunca se cargó) sin que eso signifique "no hay censo".
  const formatoFecha = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })
      : t('reports.headcount.sinDato');
  const hayAlgunaFecha = Boolean(
    data.actualizadoEnLatam || data.actualizadoEnEuropa || data.actualizadoEnUsa || data.actualizadoEn,
  );

  return (
    <div className="rr-plantilla">
      <div className="rr-plantilla-info">
        <i className="fas fa-id-card-clip rr-plantilla-icon" aria-hidden="true" />
        <div>
          <div className="rr-plantilla-title">
            {hayAlgunaFecha ? t('reports.headcount.updatedOnTitle') : t('reports.headcount.title')}
          </div>
          {hayAlgunaFecha && (
            <div className="rr-plantilla-hint">
              {t('reports.headcount.sourceLatam', { fecha: formatoFecha(data.actualizadoEnLatam) })}
              {' · '}
              {t('reports.headcount.sourceEuropa', { fecha: formatoFecha(data.actualizadoEnEuropa) })}
              {' · '}
              {t('reports.headcount.sourceUsa', { fecha: formatoFecha(data.actualizadoEnUsa) })}
            </div>
          )}
          <div className="rr-plantilla-hint">{nota ?? t('reports.headcount.hint')}</div>
        </div>
      </div>

      <div className="rr-plantilla-cifras">
        <div className="rr-plantilla-cifra">
          <div className="rr-plantilla-n">{numero(data.total)}</div>
          <div className="rr-plantilla-l">{t('reports.headcount.total')}</div>
        </div>
        <div className="rr-plantilla-cifra rr-plantilla-cifra--staff">
          <div className="rr-plantilla-n">{numero(staff)}</div>
          <div className="rr-plantilla-l">
            {t('reports.headcount.staff')} <strong>{pct(staff)}%</strong>
          </div>
        </div>
        <div className="rr-plantilla-cifra rr-plantilla-cifra--frontline">
          <div className="rr-plantilla-n">{numero(data.frontline)}</div>
          <div className="rr-plantilla-l">
            {t('reports.headcount.frontline')} <strong>{pct(data.frontline)}%</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
